import { useQuery } from "@apollo/client";
import { GraphQLFormattedError } from "graphql";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StringParam, useQueryParams } from "use-query-params";

import {
  GetInboxQuery,
  GetInboxQueryVariables,
  JoinInboxMutation,
  JoinInboxMutationVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import Loading from "~frontend/components/Loading";
import Redirect from "~frontend/components/Redirect";
import { GET_INBOX, JOIN_INBOX } from "~frontend/graphql/inboxes";
import CacheClearingApolloProvider from "~frontend/providers/CacheClearingApolloProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import LoggedInUserProvider, {
  useTenant,
} from "~frontend/providers/LoggedInUserProvider";
import useMutation from "~frontend/util/useMutation";

function InvalidLinkError(): ReactElement {
  const intl = useIntl();
  const invalidLinkError = {
    message: intl.formatMessage({
      defaultMessage:
        "Link is invalid. Please contact your organization's administrator.",
      description: "Error message for an invalid join link",
    }),
  };

  return <ErrorCard errors={[invalidLinkError]} />;
}

function JoinInbox({
  inboxId,
  token,
}: {
  inboxId: string;
  token: string;
}): ReactElement {
  interface CampaignArchivedError extends GraphQLFormattedError {
    extensions: {
      details: Record<string, string>;
    };
  }

  const intl = useIntl();
  const tenant = useTenant()!;

  const [joinInbox] = useMutation<
    JoinInboxMutation,
    JoinInboxMutationVariables
  >(JOIN_INBOX);

  const [joinInitiated, setJoinInitiated] = useState(false);
  const [joinFailed, setJoinFailed] = useState(false);
  const [joinSucceeded, setJoinSucceeded] = useState(false);
  const [campaignIsArchived, setCampaignIsArchived] = useState(false);
  const [subaccountSlug, setSubaccountSlug] = useState<string | undefined>(
    undefined
  );

  const getInboxQuery = useQuery<GetInboxQuery, GetInboxQueryVariables>(
    GET_INBOX,
    { variables: { input: { inboxId } }, skip: !joinSucceeded }
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (!joinInitiated) {
      setJoinInitiated(true);
      void (async () => {
        const response = await joinInbox({
          variables: { input: { inboxId, token } },
        });

        if (response.errors) {
          setJoinFailed(true);

          const campaignArchivedError = response.errors.find(
            e => e.extensions?.code === "CAMPAIGN_ARCHIVED"
          ) as CampaignArchivedError | undefined;

          if (campaignArchivedError) {
            setCampaignIsArchived(true);
            const {
              extensions: {
                details: { subaccountSlug },
              },
            } = campaignArchivedError;
            setSubaccountSlug(subaccountSlug);
          }
        } else if (response.data) {
          setJoinSucceeded(true);
        }
      })();
    }
  }, [
    joinInitiated,
    inboxId,
    token,
    joinInbox,
    joinFailed,
    joinSucceeded,
    campaignIsArchived,
  ]);

  if (joinFailed) {
    if (campaignIsArchived && subaccountSlug) {
      void navigate(
        tenant.defaultSubaccount
          ? `/admin/inboxes/${inboxId}`
          : `/subaccounts/${subaccountSlug}/inboxes/${inboxId}/archived`
      );
    } else {
      return <InvalidLinkError />;
    }
  }

  if (getInboxQuery.error) {
    return (
      <ErrorCard
        errors={[
          {
            message: intl.formatMessage({
              defaultMessage: "Error loading inbox. Please try again.",
              description: "Error message for loading inbox",
            }),
          },
        ]}
      />
    );
  }

  if (getInboxQuery.data) {
    return (
      <Redirect
        to={
          tenant.defaultSubaccount
            ? `/admin/inboxes/${inboxId}`
            : `/subaccounts/${getInboxQuery.data.getInbox.subaccount.slug}/inboxes/${inboxId}`
        }
      />
    );
  }

  return <Loading />;
}

function JoinSubaccount({
  token,
  subaccountId,
}: {
  token: string;
  subaccountId: string;
}): ReactElement {
  useEffect(() => {
    const urlParams = new URLSearchParams({
      screen_hint: "signup",
      join_token: token,
      join_token_subaccount: subaccountId,
    });

    document.location = `/auth/login?${urlParams.toString()}`;
  }, [token, subaccountId]);

  return <Loading />;
}

export function Join(): ReactElement {
  const [queryParams] = useQueryParams({
    inbox: StringParam,
    subaccount: StringParam,
    token: StringParam,
  });
  const { inbox, subaccount, token } = queryParams;

  if (inbox && subaccount) {
    return <InvalidLinkError />;
  }

  if (inbox && token) {
    return (
      <LoggedInUserProvider>
        <CacheClearingApolloProvider>
          <JoinInbox inboxId={inbox} token={token} />
        </CacheClearingApolloProvider>
      </LoggedInUserProvider>
    );
  }

  if (subaccount && token) {
    return <JoinSubaccount subaccountId={subaccount} token={token} />;
  }

  return <InvalidLinkError />;
}
